/**
 * The contenteditable editor behind RichTextInput: turning markdown into DOM,
 * turning the DOM back into markdown, and toggling inline formatting over a
 * Range.
 *
 * Split out of the component because none of it touches React — it was two
 * thirds of a 1335-line file, and the serialization and formatting halves call
 * into each other, so they stay together here rather than being split further.
 */
import { findDomPositionAtOffset } from "@/lib/contentEditable";
import type { CustomEmoji } from "@/lib/api/emojis";
import { CUSTOM_SHORTCODE_TOKEN_RE } from "@/lib/emoji/shortcode";

// Helpers

const URL_HIGHLIGHT_RE = /https?:\/\/[^\s]+/g;

/** Append plain text to el, wrapping any URLs in a blue highlight span. */
function appendTextWithUrlHighlights(el: HTMLElement, text: string): void {
  let lastIdx = 0;
  let m: RegExpExecArray | null;
  URL_HIGHLIGHT_RE.lastIndex = 0;
  while ((m = URL_HIGHLIGHT_RE.exec(text)) !== null) {
    if (m.index > lastIdx) {
      el.appendChild(document.createTextNode(text.slice(lastIdx, m.index)));
    }
    const span = document.createElement("span");
    span.className = "theta-url";
    span.textContent = m[0];
    el.appendChild(span);
    lastIdx = m.index + m[0].length;
  }
  if (lastIdx < text.length) {
    el.appendChild(document.createTextNode(text.slice(lastIdx)));
  }
}

/** Append text to `container`, materialising :shortcode: tokens of known custom emojis as inline imgs and bare URLs as highlighted spans. */
function appendInlineContent(
  container: HTMLElement,
  text: string,
  emojisByShortcode: Map<string, CustomEmoji>
): void {
  let lastIdx = 0;
  let m: RegExpExecArray | null;
  CUSTOM_SHORTCODE_TOKEN_RE.lastIndex = 0;
  while ((m = CUSTOM_SHORTCODE_TOKEN_RE.exec(text)) !== null) {
    const shortcode = m[1];
    const emoji = emojisByShortcode.get(shortcode);
    if (!emoji) continue;
    if (m.index > lastIdx) {
      appendTextWithUrlHighlights(container, text.slice(lastIdx, m.index));
    }
    const img = document.createElement("img");
    img.src = emoji.url;
    img.alt = `:${emoji.shortcode}:`;
    img.dataset.shortcode = emoji.shortcode;
    img.className = "theta-inline-emoji";
    img.contentEditable = "false";
    container.appendChild(img);
    lastIdx = m.index + m[0].length;
  }
  if (lastIdx < text.length) {
    appendTextWithUrlHighlights(container, text.slice(lastIdx));
  }
}

/** Build a nested strong/em/u element chain matching `tags`, appended into `root`; returns the innermost element to append content into (or `root` itself for an empty tag list). */
function appendTagChain(root: HTMLElement, tags: InlineTag[]): HTMLElement {
  let container = root;
  for (const tag of tags) {
    const el = document.createElement(tag);
    container.appendChild(el);
    container = el;
  }
  return container;
}

/** Render a plain-text markdown value into a contenteditable: :shortcode: becomes inline imgs, bare URLs get highlighted, and bold/italic/underline markdown becomes real strong/em/u DOM elements (WYSIWYG, not literal marker characters). */
export function renderTextIntoEditor(
  el: HTMLElement,
  text: string,
  emojisByShortcode: Map<string, CustomEmoji>
): void {
  el.innerHTML = "";
  if (!text) return;

  const lines = text.split("\n");
  lines.forEach((line, lineIdx) => {
    for (const segment of parseInlineMarkdown(line)) {
      const container = segment.tags.length > 0 ? appendTagChain(el, segment.tags) : el;
      appendInlineContent(container, segment.text, emojisByShortcode);
    }
    if (lineIdx < lines.length - 1) {
      el.appendChild(document.createElement("br"));
    }
  });
}

/**
 * Walk the editor's DOM and produce the raw markdown for submission.
 *
 * The zero-width spaces `insertBrAtCaret`/`toggleInlineTag` leave behind to
 * keep the caret visible are editor scaffolding, not content — stripped at
 * the text-node level in `serializeNode` (not here) so it happens before
 * `serializeChildren`'s own ZWSP insertion below is considered, and doesn't
 * strip that one back out again.
 */
export function serializeEditorToRaw(el: HTMLElement): string {
  return serializeChildren(el, "");
}

/**
 * Serialize all children of `node`, threading the output produced so far
 * (`accSoFar`) through for block-boundary detection.
 *
 * Also disambiguates adjacent sibling markers: two separately-closed/opened
 * formatting spans that happen to sit next to each other (e.g. `**foo**`
 * immediately followed by `**bar**`, or by `***bar***`) concatenate into a
 * run of `*` characters a markdown parser can't correctly re-split — e.g.
 * `**foo****bar**` cooks as one big `**foo****bar**` bold run instead of two
 * (confirmed against this instance's PrettyText). A ZWSP between them is
 * invisible in the rendered output but breaks the ambiguous run — confirmed
 * fixed the same way. Only `*`-based markers (bold/italic) collide like
 * this; `<ins>` boundaries are unambiguous HTML tags.
 */
function serializeChildren(node: Node, accSoFar: string): string {
  let result = "";
  for (const child of Array.from(node.childNodes)) {
    const chunk = serializeNode(child, accSoFar + result);
    if (!chunk) continue;
    if (result.endsWith("*") && chunk.startsWith("*")) result += "​";
    result += chunk;
  }
  return result;
}

/**
 * Would a mention token glued straight onto this output lose itself in the
 * parser? The space the serializer inserts before a chip (OSO-680) fires
 * exactly when the answer is yes:
 *
 *  - a WORD tail — the renderer's word-start guard refuses `Hej@[292]`, and
 *    the guard's `\w` is ASCII while the product is Danish, so the check here
 *    is `\p{L}\p{N}` (the parser ACCEPTS `må@[42]`, but rendering a name
 *    glued onto "må" is not what anybody meant, and the space is always safe);
 *  - an `@` tail — `@@[42]` is the one prose spelling of a literal token and
 *    must stay writable, so a chip after it gets separated;
 *  - a URL-ish tail — linkify runs BEFORE the mention rule and swallows a
 *    glued token into the href (`ogtal.dk/@[42]` becomes one corrupted link,
 *    no mention row, no notification — review of PR #384), and it does so
 *    even after `/` or `-`, which no word-character check sees. The tail
 *    heuristic over-inserts on word-with-a-dot lookalikes ("rapport.pdf"),
 *    which is the acceptable direction: a stray space versus a silently dead
 *    mention.
 *
 * Never after whitespace/nothing, and never after plain punctuation like
 * `(` or `,` — the parser accepts those, and the author placed them.
 */
function needsMentionBoundary(accSoFar: string): boolean {
  if (accSoFar === "" || /\s$/.test(accSoFar)) return false;
  if (/[\p{L}\p{N}_@]$/u.test(accSoFar)) return true;
  const tail = accSoFar.split(/\s+/).pop() ?? "";
  return /(?:https?:\/\/|www\.|[\p{L}\p{N}-]+\.[a-zA-Z]{2,})[^\s]*$/u.test(tail);
}

function serializeNode(node: Node, accSoFar: string): string {
  if (node.nodeType === Node.TEXT_NODE) return (node.textContent ?? "").replace(ZWSP_RE, "");
  if (node.nodeType !== Node.ELEMENT_NODE) return "";
  const elem = node as HTMLElement;
  switch (elem.tagName) {
    case "BR":
      return "\n";
    case "IMG":
      return elem.dataset.shortcode ? `:${elem.dataset.shortcode}:` : "";
    case "SPAN":
      // A MENTION IS `@[id]` (spec §1.2). `@handle` still parses server-side,
      // but `username` is off the wire (O-21/O-22) so the composer has no
      // handle to write and an id is the only form that cannot resolve to the
      // wrong person. The group-wide chip serializes to `@[alle]` — the one
      // canonical everyone-form the backend resolves (OSO-681).
      //
      // THE BOUNDARY IS INSERTED HERE, NOT RELAXED IN THE PARSER (OSO-680).
      // A chip can legitimately end up flush against text — the reply prefill
      // (OSO-596) puts the chip FIRST, so a greeting typed in front of it
      // reads "Hej<chip>", and deleting the space before a chip does the same
      // — but the renderer then (rightly) loses the token: its word-start
      // guard refuses `Hej@[292]`, and relaxing that guard tears bare-URL
      // autolinks apart and lets `@@[42]` mint notifications (review of
      // PR #384). So the serializer adds the single space the author's intent
      // implies — see `needsMentionBoundary` for exactly when — and the page
      // reads "Hej @Lotte", which is what they meant. The `@[alle]` chip
      // takes the same boundary, for the same reason.
      if (elem.dataset.mentionAll || elem.dataset.userId) {
        const boundary = needsMentionBoundary(accSoFar) ? " " : "";
        const token = elem.dataset.mentionAll ? "@[alle]" : `@[${elem.dataset.userId}]`;
        return `${boundary}${token}`;
      }
      return serializeChildren(elem, accSoFar);
    // THE WRAPPERS THREAD `accSoFar` THROUGH (review of PR #384): with a
    // reset to "", a chip that is the FIRST CHILD of a wrapper never saw the
    // word before it — a browser-minted plain <span> re-minted the dead
    // mention verbatim, and "Hej<strong><chip>" serialized to "Hej**@[292]**",
    // whose opening `**` is not left-flanking (followed by the punctuation
    // `@`, preceded by a letter) and renders as literal asterisks. With the
    // real prefix, the chip prepends its space INSIDE the wrapper and
    // `wrapTrimmed` — which exists to keep whitespace outside markers —
    // hoists it out: "Hej **@[292]**".
    case "STRONG":
    case "B":
      return wrapAcrossParagraphs(serializeChildren(elem, accSoFar), "**");
    case "EM":
    case "I":
      return wrapAcrossParagraphs(serializeChildren(elem, accSoFar), "*");
    case "U":
      // <ins>, not <u> — this instance's PrettyText sanitizer strips raw <u>
      // entirely (confirmed via PrettyText.cook), silently losing underline
      // on submit. <ins> is semantically "inserted text", survives the
      // sanitizer, and renders underlined by default in every browser.
      return wrapAcrossParagraphs(serializeChildren(elem, accSoFar), "<ins>", "</ins>");
    default: {
      // Block-ish elements added by the browser (e.g. on paste): treat as line breaks
      const isBlock = elem.tagName === "DIV" || elem.tagName === "P";
      const prefix = isBlock && accSoFar && !accSoFar.endsWith("\n") ? "\n" : "";
      return prefix + serializeChildren(elem, accSoFar + prefix);
    }
  }
}

/**
 * Wrap `inner` in `open`/`close` — but if `inner` spans one or more
 * blank-line paragraph breaks (two consecutive `<br>`s inside the same
 * formatting element, e.g. from pressing Enter twice while bold is still
 * on), close and reopen around each blank line instead of emitting one
 * continuous run. Markdown emphasis (and HTML block parsing generally)
 * cannot cross a blank line — a marker opened in one paragraph and closed
 * in a much later one is invalid and never pairs up correctly (confirmed
 * against this instance's PrettyText: opening/closing on either side of a
 * blank line renders as literal, unpaired characters, while closing and
 * reopening each paragraph renders each one correctly formatted).
 */
function wrapAcrossParagraphs(inner: string, open: string, close: string = open): string {
  if (!inner) return "";
  return inner
    .split("\n\n")
    .map((part) => (part ? wrapTrimmed(part, open, close) : part))
    .join("\n\n");
}

/**
 * Wrap `part` in `open`/`close`, but keep any leading/trailing whitespace
 * OUTSIDE the markers. CommonMark requires an opening delimiter be
 * immediately followed by non-whitespace (and a closing one immediately
 * preceded by non-whitespace) to count as valid emphasis at all — e.g. a
 * `<strong>` whose content starts with a `<br>` (pressing Enter right after
 * opening bold, before typing anything) serializes to `**\ntext**`, which
 * isn't just wrongly-placed, it's not emphasis at all: `**` next to a line
 * break renders as two literal asterisks (confirmed against this instance's
 * PrettyText). Moving the leading newline outside — `\n**text**` — fixes it.
 * Same reasoning applies to a plain leading/trailing space from a selection
 * that happened to include one.
 */
function wrapTrimmed(part: string, open: string, close: string): string {
  const leading = part.match(/^\s+/)?.[0] ?? "";
  const trailing = part.match(/\s+$/)?.[0] ?? "";
  // slice() safely returns "" if leading+trailing overlap (part is all whitespace).
  const core = part.slice(leading.length, part.length - trailing.length);
  return core ? `${leading}${open}${core}${close}${trailing}` : part;
}

/** Zero-width spaces the editor plants to keep the caret visible after a <br>. */
const ZWSP_RE = /​/g;

/**
 * A mention chip plus the space that follows it, as a fragment ready to
 * insert — and the text it adds, so the caller can move the caret past it.
 *
 * ONE BUILDER for both ways a mention gets into the editor: picking a name
 * from the autocomplete, and the reply composer's prefill (OSO-596). The chip
 * carries `data-user-id` and nothing else identifying — `serializeNode` above
 * turns that into `@[id]`, which is the only form that cannot resolve to the
 * wrong person (O-21/O-22).
 */
export function mentionFragment(
  userId: number,
  displayName: string
): { fragment: DocumentFragment; text: string } {
  const span = document.createElement("span");
  span.className = "theta-mention";
  span.dataset.userId = String(userId);
  span.contentEditable = "false";
  span.textContent = `@${displayName}`;

  const fragment = document.createDocumentFragment();
  fragment.appendChild(span);
  fragment.appendChild(document.createTextNode(" "));
  return { fragment, text: `@${displayName} ` };
}

/**
 * The `@alle` chip (OSO-681) — same shape as a person's chip, but carrying
 * `data-mention-all` instead of a user id, which `serializeNode` above turns
 * into the canonical `@[alle]`. Only offered to group admins/moderators
 * (the dropdown gates on `can.moderate`); the server enforces the same rule
 * at write time, so the chip is a suggestion, not a permission.
 */
export function mentionEveryoneFragment(): { fragment: DocumentFragment; text: string } {
  const span = document.createElement("span");
  span.className = "theta-mention";
  span.dataset.mentionAll = "true";
  span.contentEditable = "false";
  span.textContent = "@alle";

  const fragment = document.createDocumentFragment();
  fragment.appendChild(span);
  fragment.appendChild(document.createTextNode(" "));
  return { fragment, text: "@alle " };
}

/** Replace the editor's text-rep range [start, end) with a DocumentFragment. */
export function replaceTextRangeWithFragment(
  root: HTMLElement,
  start: number,
  end: number,
  fragment: DocumentFragment
): void {
  const startPos = findDomPositionAtOffset(root, start);
  const endPos = findDomPositionAtOffset(root, end);
  if (!startPos || !endPos) return;
  const range = document.createRange();
  range.setStart(startPos.node, startPos.offset);
  range.setEnd(endPos.node, endPos.offset);
  range.deleteContents();
  range.insertNode(fragment);
}

/** Insert a <br> at the current caret. */
export function insertBrAtCaret(): void {
  const sel = window.getSelection();
  if (!sel || sel.rangeCount === 0) return;
  const range = sel.getRangeAt(0);
  range.deleteContents();
  const br = document.createElement("br");
  range.insertNode(br);
  // Move caret after the <br> — also insert a zero-width space to keep the
  // caret visible at the end of the new line in Chrome/Firefox.
  const after = document.createTextNode("\u200b");
  br.parentNode!.insertBefore(after, br.nextSibling);
  range.setStartAfter(after);
  range.collapse(true);
  sel.removeAllRanges();
  sel.addRange(range);
}

// Formatting (bold / italic / underline)
//
// Real WYSIWYG formatting: toggling bold/italic/underline wraps or unwraps
// the selected DOM Range in a <strong>/<em>/<u> element, so the editor shows
// actual bold/italic/underlined text immediately. serializeEditorToRaw turns
// those elements back into **/*/<u> markdown on submit; renderTextIntoEditor
// does the inverse when loading existing markdown (e.g. editing a post).

export type InlineTag = "strong" | "em" | "u";

const INLINE_TAG_SELECTOR: Record<InlineTag, string> = {
  strong: "strong, b",
  em: "em, i",
  u: "u",
};

interface InlineSegment {
  text: string;
  tags: InlineTag[];
}

function addTag(tags: InlineTag[], tag: InlineTag): InlineTag[] {
  return tags.includes(tag) ? tags : [...tags, tag];
}

/** Find the next `marker` at or after `from` whose preceding character isn't whitespace (a lightweight flanking rule — keeps "3 * 4 * 5" from being misread as emphasis). Returns -1 if none. */
function findMarkerClose(text: string, from: number, marker: string): number {
  let idx = from;
  while (true) {
    idx = text.indexOf(marker, idx);
    if (idx === -1) return -1;
    if (idx > from && !/\s/.test(text[idx - 1])) return idx;
    idx += marker.length;
  }
}

/**
 * Small, pragmatic inline-markdown scanner — bold (`**`), italic (`*`, not
 * part of a `**` run) and underline (`<ins>...</ins>` — not `<u>`, which this
 * instance's sanitizer strips; see serializeNode's "U" case) only, with
 * simple recursive nesting (e.g. `**bold *and* italic**`). Not a CommonMark
 * implementation: links, code spans, lists etc. are out of scope and pass
 * through as literal text, same as before this scanner existed. Pure
 * string → data, used by `renderTextIntoEditor` to build real DOM elements
 * instead of showing literal marker characters when loading existing content
 * (e.g. editing a post).
 */
export function parseInlineMarkdown(text: string, activeTags: InlineTag[] = []): InlineSegment[] {
  const segments: InlineSegment[] = [];
  let i = 0;
  let plainStart = 0;

  const flushPlain = (end: number) => {
    if (end > plainStart) segments.push({ text: text.slice(plainStart, end), tags: activeTags });
  };

  while (i < text.length) {
    if (text.startsWith("**", i) && text[i + 2] && !/\s/.test(text[i + 2])) {
      const close = findMarkerClose(text, i + 2, "**");
      if (close !== -1) {
        flushPlain(i);
        segments.push(...parseInlineMarkdown(text.slice(i + 2, close), addTag(activeTags, "strong")));
        i = close + 2;
        plainStart = i;
        continue;
      }
    } else if (text[i] === "*" && text[i + 1] && !/\s/.test(text[i + 1])) {
      const close = findMarkerClose(text, i + 1, "*");
      if (close !== -1) {
        flushPlain(i);
        segments.push(...parseInlineMarkdown(text.slice(i + 1, close), addTag(activeTags, "em")));
        i = close + 1;
        plainStart = i;
        continue;
      }
    } else if (text.startsWith("<ins>", i)) {
      const close = text.indexOf("</ins>", i + 5);
      if (close !== -1 && close > i + 5) {
        flushPlain(i);
        segments.push(...parseInlineMarkdown(text.slice(i + 5, close), addTag(activeTags, "u")));
        i = close + 6;
        plainStart = i;
        continue;
      }
    }
    i++;
  }
  flushPlain(text.length);
  return segments;
}

function nodeLength(n: Node): number {
  return n.nodeType === Node.TEXT_NODE ? (n.textContent?.length ?? 0) : n.childNodes.length;
}

function rangeIntersectsNode(range: Range, node: Node): boolean {
  if (typeof range.intersectsNode === "function") return range.intersectsNode(node);
  const nodeRange = document.createRange();
  nodeRange.selectNode(node);
  return (
    range.compareBoundaryPoints(Range.END_TO_START, nodeRange) < 0 &&
    range.compareBoundaryPoints(Range.START_TO_END, nodeRange) > 0
  );
}

/** Split text nodes at the range's boundaries so both ends land on a node edge. */
function splitRangeBoundaries(range: Range): void {
  const endNode = range.endContainer;
  const endOffset = range.endOffset;
  if (
    endNode.nodeType === Node.TEXT_NODE &&
    endOffset > 0 &&
    endOffset < (endNode.textContent?.length ?? 0)
  ) {
    (endNode as Text).splitText(endOffset);
    // Range boundaries auto-adjust when a contained text node splits.
  }
  const startNode = range.startContainer;
  const startOffset = range.startOffset;
  if (
    startNode.nodeType === Node.TEXT_NODE &&
    startOffset > 0 &&
    startOffset < (startNode.textContent?.length ?? 0)
  ) {
    const newNode = (startNode as Text).splitText(startOffset);
    range.setStart(newNode, 0);
  }
}

/** Promote each boundary up to its parent while it sits exactly at that node's edge. */
function promoteRangeBoundaries(range: Range, root: HTMLElement): void {
  let sc: Node = range.startContainer;
  let so = range.startOffset;
  while (sc !== root && so === 0 && sc.parentNode) {
    so = Array.prototype.indexOf.call(sc.parentNode.childNodes, sc);
    sc = sc.parentNode;
  }
  let ec: Node = range.endContainer;
  let eo = range.endOffset;
  while (ec !== root && eo === nodeLength(ec) && ec.parentNode) {
    eo = Array.prototype.indexOf.call(ec.parentNode.childNodes, ec) + 1;
    ec = ec.parentNode;
  }
  range.setStart(sc, so);
  range.setEnd(ec, eo);
}

/**
 * Normalize a Range's boundaries onto element-child edges. `Range.
 * extractContents()`/`cloneContents()` silently drop ancestor context when
 * both endpoints sit inside the same text node (e.g. selecting "text" out of
 * `<strong>bold text</strong>` yields a bare "text" fragment, no `<strong>`)
 * — normalizing first is what lets `toggleInlineTag` correctly split a
 * formatted run at the selection boundary instead of nesting a duplicate tag.
 */
function normalizeRangeBoundaries(range: Range, root: HTMLElement): void {
  splitRangeBoundaries(range);
  promoteRangeBoundaries(range, root);
}

/** Remove every element matching `tag` (or its legacy alias) inside fragment, keeping its children in place. */
function stripInlineTag(fragment: DocumentFragment, tag: InlineTag): void {
  let el: HTMLElement | null;
  while ((el = fragment.querySelector(INLINE_TAG_SELECTOR[tag]))) {
    const parent = el.parentNode!;
    while (el.firstChild) parent.insertBefore(el.firstChild, el);
    parent.removeChild(el);
  }
}

/**
 * True if every non-whitespace leaf of content intersecting `range` already
 * sits inside a `tag` ancestor. Purely read-only — walks the live DOM via
 * ancestor `.closest()` checks, no cloning or splitting, so it's safe to call
 * on every selection change (e.g. toolbar active-state highlighting) without
 * mutating the editor.
 */
export function isRangeFullyWrapped(range: Range, tag: InlineTag): boolean {
  const selector = INLINE_TAG_SELECTOR[tag];
  if (range.collapsed) {
    const node = range.startContainer;
    const el = node.nodeType === Node.ELEMENT_NODE ? (node as HTMLElement) : node.parentElement;
    return !!el?.closest(selector);
  }
  const anchor = range.commonAncestorContainer;
  const walkerRoot = anchor.nodeType === Node.ELEMENT_NODE ? anchor : anchor.parentNode;
  if (!walkerRoot) return false;
  const walker = document.createTreeWalker(
    walkerRoot,
    NodeFilter.SHOW_TEXT | NodeFilter.SHOW_ELEMENT
  );
  let hasContent = false;
  let allWrapped = true;
  let node: Node | null = walker.currentNode;
  while (node) {
    if (rangeIntersectsNode(range, node)) {
      if (node.nodeType === Node.TEXT_NODE) {
        if ((node.textContent ?? "").trim().length > 0) {
          hasContent = true;
          if (!node.parentElement?.closest(selector)) allWrapped = false;
        }
      } else if ((node as HTMLElement).tagName === "IMG") {
        hasContent = true;
        if (!(node as HTMLElement).closest(selector)) allWrapped = false;
      }
    }
    node = walker.nextNode();
  }
  return hasContent && allWrapped;
}

/** Collapse the selection to a point immediately after `node`. */
function setCollapsedSelectionAfter(node: Node): void {
  const newRange = document.createRange();
  newRange.setStartAfter(node);
  newRange.collapse(true);
  const sel = window.getSelection();
  sel?.removeAllRanges();
  sel?.addRange(newRange);
}

/** True if `text` is non-empty but has nothing left after stripping ZWSPs — i.e. it's pure caret-placeholder scaffolding, not real content. */
function isBareZwsp(text: string | null): boolean {
  return !!text && text.replace(ZWSP_RE, "").length === 0;
}

/**
 * Remove a bare-ZWSP scaffold marker immediately touching `range`'s
 * collapsed position, if any, and restore the caret to that exact spot.
 * Checked on both possible boundary representations a browser might report
 * (the text node itself, or its parent + an offset pointing at it as a
 * sibling). Without this, opening a tag right where a previous open/exit
 * toggle left an unused marker (repeated presses with nothing typed in
 * between) would insert a second marker alongside the first instead of
 * reusing the spot, growing a new empty tag on every press.
 */
function consumeAdjacentBareZwsp(range: Range): void {
  const container = range.startContainer;
  let zwsp: Text | null = null;
  if (container.nodeType === Node.TEXT_NODE) {
    if (isBareZwsp(container.textContent)) zwsp = container as Text;
  } else if (container.nodeType === Node.ELEMENT_NODE) {
    const before = container.childNodes[range.startOffset - 1];
    const after = container.childNodes[range.startOffset];
    for (const cand of [before, after]) {
      if (cand?.nodeType === Node.TEXT_NODE && isBareZwsp(cand.textContent)) {
        zwsp = cand as Text;
        break;
      }
    }
  }
  if (!zwsp?.parentNode) return;
  const parent = zwsp.parentNode;
  const idx = Array.prototype.indexOf.call(parent.childNodes, zwsp);
  parent.removeChild(zwsp);
  range.setStart(parent, idx);
  range.setEnd(parent, idx);
}

/** Open an empty `<tag>` (with a ZWSP, the same trick `insertBrAtCaret` uses to keep the caret visible/typeable) at a collapsed caret, and place the caret inside it so subsequently typed text lands already formatted. */
function openCollapsedTag(range: Range, tag: InlineTag): void {
  consumeAdjacentBareZwsp(range);
  const el = document.createElement(tag);
  const zwsp = document.createTextNode("​");
  el.appendChild(zwsp);
  range.insertNode(el);
  const newRange = document.createRange();
  newRange.setStart(zwsp, 1);
  newRange.collapse(true);
  const sel = window.getSelection();
  sel?.removeAllRanges();
  sel?.addRange(newRange);
}

/**
 * Exit `tag` at a collapsed caret already inside it, so subsequently typed
 * text is no longer formatted — without touching text before or after the
 * caret (both keep whatever formatting they already had). Splits the
 * enclosing `tag` element in two around the caret (re-wrapping the "after"
 * half in a fresh `<tag>`, since it can no longer share the original
 * element) and inserts a plain ZWSP marker between them for the caret.
 *
 * This is the missing half of the collapsed-caret toggle: without it,
 * toggling off at a caret already inside `tag` (e.g. pressing Cmd+B again to
 * stop typing bold) fell through to `openCollapsedTag`, which nests another
 * empty `<tag>` *inside* the existing one instead of exiting it — repeat
 * presses compound into deeply nested empty tags (OSO-313).
 */
function exitCollapsedTag(range: Range, tag: InlineTag): void {
  const container = range.startContainer;
  const anchorEl = container.nodeType === Node.ELEMENT_NODE ? (container as HTMLElement) : container.parentElement;
  const tagEl = anchorEl?.closest(INLINE_TAG_SELECTOR[tag]) as HTMLElement | null;
  if (!tagEl || !tagEl.parentNode) {
    openCollapsedTag(range, tag);
    return;
  }

  // Everything from the caret to the end of tagEl's content — extracting it
  // shrinks tagEl down to just the "before caret" part, leaving the "after
  // caret" part (with any of its own nested formatting, e.g. em inside u)
  // in the extracted fragment.
  const tailRange = document.createRange();
  tailRange.setStart(container, range.startOffset);
  tailRange.setEndAfter(tagEl.lastChild ?? tagEl);
  const tailContent = tailRange.extractContents();

  const zwsp = document.createTextNode("​");
  tagEl.after(zwsp);
  if (fragmentHasContent(tailContent)) {
    // Re-wrap the tail in a fresh `tag` — it's no longer inside tagEl, but it
    // keeps the formatting it had, same as text before the caret does.
    const tailWrapper = document.createElement(tag);
    tailWrapper.appendChild(tailContent);
    zwsp.after(tailWrapper);
  }
  if (!tagEl.textContent) tagEl.remove();

  setCollapsedSelectionAfter(zwsp);
}

/**
 * True if `fragment` has any real content — as opposed to the empty text
 * node `extractContents()` can leave behind when a range's start and end
 * describe the same boundary point via different (container, offset) pairs
 * (e.g. end-of-text-node vs. end-of-parent's-child-list): the two compare
 * equal, so nothing should be extracted, but some engines still produce a
 * zero-length text node artifact. Left unfiltered, that artifact reads as
 * "there's a tail" and produces a stray empty wrapper element. Checked
 * recursively: the same artifact can surface one level down too, as an
 * empty *element* (e.g. a bare `<u></u>`) wrapping nothing — extractContents
 * clones an ancestor to preserve its context even when there's no actual
 * content inside it at that boundary. An IMG counts as content even with no
 * text (e.g. an inline custom emoji); an empty wrapper element does not.
 */
function fragmentHasContent(fragment: DocumentFragment | HTMLElement): boolean {
  for (const node of Array.from(fragment.childNodes)) {
    if (node.nodeType === Node.TEXT_NODE) {
      if ((node.textContent ?? "").replace(ZWSP_RE, "").length > 0) return true;
    } else if (node.nodeType === Node.ELEMENT_NODE) {
      if ((node as HTMLElement).tagName === "IMG") return true;
      if (fragmentHasContent(node as HTMLElement)) return true;
    }
  }
  return false;
}

/**
 * Toggle `tag` over `range` within `root` — real WYSIWYG DOM formatting, not
 * markdown characters.
 *  - Collapsed selection: opens an empty `<tag>` to type into (see
 *    `openCollapsedTag`), or exits an enclosing one (see `exitCollapsedTag`)
 *    if the caret already sits inside a matching tag.
 *  - Non-collapsed: normalizes the range onto element boundaries so
 *    `extractContents()` correctly splits any run the selection only
 *    partially covers, then always strips existing `tag` wrappers from the
 *    extracted fragment (this both implements "turn off" and clears any
 *    partial/nested wrapping before a "turn on" re-wrap), and re-wraps it
 *    only if the selection wasn't already fully wrapped.
 */
export function toggleInlineTag(root: HTMLElement, range: Range, tag: InlineTag): void {
  if (range.collapsed) {
    const container = range.startContainer;
    const anchorEl = container.nodeType === Node.ELEMENT_NODE ? (container as HTMLElement) : container.parentElement;
    const tagEl = anchorEl?.closest(INLINE_TAG_SELECTOR[tag]) as HTMLElement | null;
    if (tagEl && isBareZwsp(tagEl.textContent) && !tagEl.querySelector("img")) {
      // Nothing has been typed since this tag was opened — just remove the
      // empty scaffold instead of going through the general "exit" split
      // (which has nothing real to split and would otherwise leave a fresh
      // marker behind on every press with nothing typed in between).
      const zwsp = document.createTextNode("​");
      tagEl.replaceWith(zwsp);
      setCollapsedSelectionAfter(zwsp);
    } else if (tagEl) {
      exitCollapsedTag(range, tag);
    } else {
      openCollapsedTag(range, tag);
    }
    return;
  }

  const wrapped = isRangeFullyWrapped(range, tag);
  normalizeRangeBoundaries(range, root);
  const fragment = range.extractContents();
  stripInlineTag(fragment, tag);

  let toInsert: DocumentFragment | HTMLElement = fragment;
  if (!wrapped) {
    const wrapper = document.createElement(tag);
    wrapper.appendChild(fragment);
    toInsert = wrapper;
  }

  const first = toInsert.firstChild;
  const last = toInsert.lastChild;
  range.insertNode(toInsert);

  if (first && last) {
    const newRange = document.createRange();
    newRange.setStartBefore(first);
    newRange.setEndAfter(last);
    const sel = window.getSelection();
    sel?.removeAllRanges();
    sel?.addRange(newRange);
  }
}

/**
 * "KÆRE <NAVN>" LIT UP IN THE EDITOR — RichTextInput `highlightLeading`.
 *
 * The first `length` characters after any leading whitespace are wrapped in
 * `<span class="theta-bot-address">`, which globals.css paints with the hero
 * gradient in bold; `null` unwraps. A span, not a CSS highlight, because the
 * ask is a gradient and a weight, and the Custom Highlight API can set
 * neither. The serializers treat a plain span as its text, so what is sent
 * is unaffected.
 *
 * IDEMPOTENT ON PURPOSE. This runs after every keystroke, and touching the
 * DOM moves the caret — so when the span already covers exactly the right
 * text nothing happens and `false` comes back. The caller restores the caret
 * only when `true` says the DOM changed. Typing right after the greeting
 * lands INSIDE the span (the browser extends the inline element); the next
 * pass splits that character back out, once, and the caret is then in the
 * outer text node where later keystrokes stay.
 *
 * Only a greeting that sits whole inside one text node is wrapped. Split
 * across a mention chip, an emoji image or a formatting boundary it is not
 * the plain "Kære <navn>" the caller matched, and nothing is lit.
 */
export const BOT_ADDRESS_CLASS = "theta-bot-address";

export function applyLeadingHighlight(root: HTMLElement, length: number | null): boolean {
  const existing = Array.from(root.querySelectorAll<HTMLElement>(`span.${BOT_ADDRESS_CLASS}`));
  if (length && existing.length === 1 && coversLeadingText(root, existing[0]!, length)) {
    return false;
  }

  let changed = false;
  for (const span of existing) {
    const parent = span.parentNode;
    if (!parent) continue;
    while (span.firstChild) parent.insertBefore(span.firstChild, span);
    parent.removeChild(span);
    changed = true;
  }
  if (changed) root.normalize();
  if (!length) return changed;

  const target = leadingText(root);
  if (!target || target.node.length - target.start < length) return changed;

  const lead = target.start > 0 ? target.node.splitText(target.start) : target.node;
  if (lead.length > length) lead.splitText(length);
  const span = root.ownerDocument.createElement("span");
  span.className = BOT_ADDRESS_CLASS;
  lead.parentNode!.insertBefore(span, lead);
  span.appendChild(lead);
  return true;
}

/** The first text node holding a non-whitespace character, and where in it. */
function leadingText(root: HTMLElement): { node: Text; start: number } | null {
  const walker = root.ownerDocument.createTreeWalker(root, NodeFilter.SHOW_TEXT);
  for (let node = walker.nextNode(); node; node = walker.nextNode()) {
    const text = node.textContent ?? "";
    const start = text.search(/\S/);
    if (start !== -1) return { node: node as Text, start };
  }
  return null;
}

/** Is `span` exactly `length` characters of text, with only whitespace before it? */
function coversLeadingText(root: HTMLElement, span: HTMLElement, length: number): boolean {
  if (span.childNodes.length !== 1 || span.firstChild?.nodeType !== Node.TEXT_NODE) return false;
  if ((span.textContent ?? "").length !== length) return false;
  const walker = root.ownerDocument.createTreeWalker(root, NodeFilter.SHOW_TEXT);
  for (let node = walker.nextNode(); node; node = walker.nextNode()) {
    if (node === span.firstChild) return true;
    if (/\S/.test(node.textContent ?? "")) return false;
  }
  return false;
}
